import { useMemo } from 'react';
import { Button } from '@accure/ui';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { compareDesc, startOfMonth } from 'date-fns';
import { Loader2, Search } from 'lucide-react';

import {
  type Communication,
  type CommunicationPagination,
} from '@/api/fe-api/entities/communication';

import { filtersEmptyState, useListFilters } from '../hooks/use-list-filters';

import { CommunicationSidebarListGroup } from './communication-sidebar-list-group';
import { Skeleton } from './ui/skeleton';

export type CommunicationSidebarListProps = {
  listResult: UseInfiniteQueryResult<CommunicationPagination>;
};

export function CommunicationSidebarList({
  listResult,
}: CommunicationSidebarListProps) {
  const [_, setFilters, filtersStatus] = useListFilters();
  const communicationsPerMonth = useMemo(
    function () {
      const communications =
        listResult.data?.pages.flatMap((page) => page.communications) ?? [];

      return groupCommunications(communications);
    },
    [listResult.data],
  );

  if (listResult.isLoading) {
    return <Loading.LoadingSkeleton />;
  }

  if (listResult.isError) {
    throw listResult.error;
  }

  if (communicationsPerMonth.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <div className="mb-4">
          <Search className="mx-auto size-12 text-gray-400" />
        </div>
        <h3 className="mb-2 text-lg font-medium text-gray-800">
          No results found
        </h3>
        <p className="mb-4 text-sm text-gray-600">
          No communications match your current search criteria.
        </p>
        {filtersStatus.active ? (
          <Button
            onClick={() => setFilters.replace(filtersEmptyState)}
            variant="link"
            className="h-auto p-0 text-sm hover:underline"
          >
            Reset Filter
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {communicationsPerMonth.map(([month, communications]) => (
        <CommunicationSidebarListGroup
          key={month}
          month={new Date(month)}
          communications={communications}
        />
      ))}

      {listResult.hasNextPage ? (
        <div className="flex items-center justify-center py-3">
          <button
            onClick={() => listResult.fetchNextPage()}
            disabled={listResult.isFetchingNextPage}
            className="flex items-center gap-1 rounded-md px-3 py-1.5 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary-600"
          >
            {listResult.isFetchingNextPage ? (
              <>
                <Loader2 className="size-3 animate-spin" />
                <span>Loading more communications...</span>
              </>
            ) : (
              <>
                <span>Load more communications</span>
              </>
            )}
          </button>
        </div>
      ) : null}
    </div>
  );
}

type GroupedCommunications = [number, Communication[]][];

function groupCommunications(
  communications: Communication[],
): GroupedCommunications {
  const sortedCommunications = [...communications].sort((a, b) =>
    compareDesc(a.date, b.date),
  );

  const list = new Map<number, Communication[]>();

  for (const communication of sortedCommunications) {
    const month = startOfMonth(communication.date).getTime();

    if (!list.has(month)) {
      list.set(month, []);
    }

    list.get(month)?.push(communication);
  }

  return Array.from(list.entries());
}

namespace Loading {
  function ListItem() {
    return (
      <li className="px-4 py-3">
        <div className="flex items-center">
          <Skeleton className="mr-3 size-5 rounded-full" />
          <div className="flex-1">
            <Skeleton className="mb-2 h-4 w-3/4" />
            <Skeleton className="mb-1 h-3 w-1/2" />
            <Skeleton className="h-3 w-2/3" />
          </div>
          <Skeleton className="ml-2 size-4" />
        </div>
      </li>
    );
  }

  // Skeleton loader for month groups
  function MonthGroup({ itemCount = 3 }) {
    return (
      <div className="pb-2">
        <div className="sticky top-0 z-10 bg-gray-100 px-4 py-2">
          <Skeleton className="h-4 w-32" />
        </div>
        <ul className="divide-y divide-gray-100">
          {Array(itemCount)
            .fill(0)
            .map((_, index) => (
              <ListItem key={index} />
            ))}
        </ul>
      </div>
    );
  }

  export function LoadingSkeleton() {
    return (
      <div className="divide-y divide-gray-200">
        <MonthGroup itemCount={4} />
        <MonthGroup itemCount={3} />
      </div>
    );
  }
}
